import formatInTimeZone from 'date-fns-tz/formatInTimeZone';
import type { StoreActionApi } from 'react-sweet-state';

import { fireTrackAnalytics } from '@atlassian/analytics-bridge';
import { errorToastProps, successToastProps } from '@atlassian/eoc-common';
import { NavigationMode, NavigationPeriodId, zoned } from '@atlassian/eoc-schedule-common';

import { messages as commonMessages } from '../../../common/messages';
import { PageModalType, type RotationDetail } from '../../../common/types';
import { endpoints as scheduleEndpoints } from '../../../services/endpoints/schedule';
import {
	type AddOverrideBulkParameters,
	type AddOverrideParameters,
	type GetOverrideParameters,
	type RevertOverrideParameters,
	type TakeOnCallParameters,
	type UpdateOverrideParameters,
} from '../../../services/endpoints/schedule/types';
import { isEmpty } from '../../../utils/validators';
import type { ScheduleStore } from '../state/types';
import type { WrapperProps } from '../types';

import { messages } from './messages';
import {
	fromCalendarLayerWeekToWeek,
	fromPeriodResponseToPeriod,
	fromRotationDetailToRotationPayload,
} from './utils';

const getTimelineAction =
	() =>
	async (
		{ getState, setState }: StoreActionApi<ScheduleStore>,
		{ flagApi, intlApi, defaultCatch }: WrapperProps,
	) => {
		const { schedule, currentDate, selectedView, timelineType, rotation } = getState();

		setState({ getTimelineLoading: true });

		const formattedCurrent = formatInTimeZone(currentDate, 'UTC', "yyyy-MM-dd'T'HH:mmxxx");

		try {
			if (selectedView === NavigationMode.Timeline) {
				const response = await scheduleEndpoints.getTimeline({
					scheduleId: schedule.id,
					currentDate: formattedCurrent,
					mode: selectedView,
					timelineType,
					...(!isEmpty(rotation) && {
						rotation: fromRotationDetailToRotationPayload(rotation),
					}),
				});

				setState({
					timelineData: {
						startEpoch: response.startTimeUtcMillis,
						endEpoch: response.endTimeUtcMillis,
						nowEpoch: response.currentTime,
						startOffset: response.startOffsetMillis,
						endOffset: response.endOffsetMillis,
						nowOffset: response.utcTimeOffsetMillis,
						hasDeletedResponders: response.hasDeletedResponders,
						// TODO: Rename to 'baseRotations'
						defaultLayers: (response.scheduleLayer?.rotations ?? []).map(
							({ id, name, periods, hasDeletedResponders }) => ({
								id,
								name,
								hasDeletedResponders,
								periods: periods.map((period) => fromPeriodResponseToPeriod(period)),
							}),
						),
						overrideLayers: (response.overrideLayer?.rotations ?? []).map(
							({ id, name, periods, hasDeletedResponders }) => ({
								id,
								name,
								hasDeletedResponders,
								periods: periods.map((period) => fromPeriodResponseToPeriod(period)),
							}),
						),
						forwardingLayers: (response.forwardingLayer?.rotations ?? []).map(
							({ id, name, periods, hasDeletedResponders }) => ({
								id,
								name,
								hasDeletedResponders,
								periods: periods.map((period) => fromPeriodResponseToPeriod(period)),
							}),
						),
						finalLayers: (response.finalLayer?.rotations ?? []).map(
							({ id, name, periods, hasDeletedResponders }) => ({
								id,
								name,
								hasDeletedResponders,
								periods: periods.map((period) => fromPeriodResponseToPeriod(period)),
							}),
						),
					},
					calendarData: undefined,
				});
			} else if (selectedView === NavigationMode.Calendar) {
				const response = await scheduleEndpoints.getCalendar({
					scheduleId: schedule.id,
					currentDate: formattedCurrent,
					mode: selectedView,
					timelineType: NavigationPeriodId.OneMonth,
					...(!isEmpty(rotation) && {
						rotation: fromRotationDetailToRotationPayload(rotation),
					}),
				});

				setState({
					timelineData: undefined,
					calendarData: {
						startEpoch: response.startTimeUtcMillis,
						endEpoch: response.endTimeUtcMillis,
						nowEpoch: response.currentTime,
						startOffset: response.startOffsetMillis,
						endOffset: response.endOffsetMillis,
						nowOffset: response.utcTimeOffsetMillis,
						hasDeletedResponders: response.hasDeletedResponders,
						baseWeeks: fromCalendarLayerWeekToWeek(response.scheduleLayer.weeks),
						finalWeeks: fromCalendarLayerWeekToWeek(response.finalLayer.weeks),
					},
				});
			}
		} catch (error) {
			defaultCatch<void>(
				{
					id: 'getTimelineAction',
					httpErrorHandler: (err) => {
						if (err.message) {
							flagApi.showFlag({
								...errorToastProps(),
								description: err.message,
								title: intlApi.formatMessage(commonMessages.error),
							});
						} else {
							flagApi.showFlag({
								...errorToastProps(),
								title: intlApi.formatMessage(messages.getLayersErrorTitle),
								description: intlApi.formatMessage(messages.getLayersErrorDescription),
							});
						}
					},
				},
				error,
			);
		} finally {
			setState({ getTimelineLoading: false });
		}
	};

const setRotationAction =
	(rotation?: RotationDetail | undefined) =>
	({ setState, dispatch }: StoreActionApi<ScheduleStore>) => {
		setState({ rotation });
		dispatch(getTimelineAction());
	};

const setSelectedViewAction =
	(selectedView: NavigationMode) =>
	({ setState, dispatch }: StoreActionApi<ScheduleStore>) => {
		setState({ selectedView });
		dispatch(getTimelineAction());
	};

const setTimelineTypeAction =
	(timelineType: NavigationPeriodId) =>
	({ setState, dispatch }: StoreActionApi<ScheduleStore>) => {
		setState({ timelineType });
		dispatch(getTimelineAction());
	};

const previousPeriodAction =
	() =>
	async ({ getState, setState, dispatch }: StoreActionApi<ScheduleStore>) => {
		const {
			currentDate,
			timelineType,
			selectedView,
			schedule: { timezone },
		} = getState();

		switch (selectedView) {
			case NavigationMode.Calendar:
				setState({
					currentDate: zoned.midOfDay(zoned.previousMonth(currentDate, timezone), timezone),
				});
				break;

			case NavigationMode.Timeline:
				switch (timelineType) {
					case NavigationPeriodId.OneDay:
						setState({
							currentDate: zoned.midOfDay(zoned.previousDay(currentDate, timezone), timezone),
						});
						break;
					case NavigationPeriodId.OneWeek:
						setState({
							currentDate: zoned.midOfDay(zoned.previousWeek(currentDate, timezone), timezone),
						});
						break;
					case NavigationPeriodId.TwoWeeks:
						setState({
							currentDate: zoned.midOfDay(zoned.previousTwoWeeks(currentDate, timezone), timezone),
						});
						break;
					case NavigationPeriodId.OneMonth:
						setState({
							currentDate: zoned.midOfDay(zoned.previousMonth(currentDate, timezone), timezone),
						});
				}
				break;
		}

		dispatch(getTimelineAction());
	};

const nextPeriodAction =
	() =>
	async ({ getState, setState, dispatch }: StoreActionApi<ScheduleStore>) => {
		const {
			currentDate,
			timelineType,
			selectedView,
			schedule: { timezone },
		} = getState();

		switch (selectedView) {
			case NavigationMode.Calendar:
				setState({
					currentDate: zoned.midOfDay(zoned.nextMonth(currentDate, timezone), timezone),
				});
				break;

			case NavigationMode.Timeline:
				switch (timelineType) {
					case NavigationPeriodId.OneDay:
						setState({
							currentDate: zoned.midOfDay(zoned.nextDay(currentDate, timezone), timezone),
						});
						break;
					case NavigationPeriodId.OneWeek:
						setState({
							currentDate: zoned.midOfDay(zoned.nextWeek(currentDate, timezone), timezone),
						});
						break;
					case NavigationPeriodId.TwoWeeks:
						setState({
							currentDate: zoned.midOfDay(zoned.nextTwoWeeks(currentDate, timezone), timezone),
						});
						break;
					case NavigationPeriodId.OneMonth:
						setState({
							currentDate: zoned.midOfDay(zoned.nextMonth(currentDate, timezone), timezone),
						});
				}
				break;
		}

		dispatch(getTimelineAction());
	};

const takeOnCallAction =
	({ accountId }: Omit<TakeOnCallParameters, 'scheduleId'>) =>
	async (
		{ dispatch, setState, getState }: StoreActionApi<ScheduleStore>,
		{ flagApi, intlApi, closePageModal, defaultCatch }: WrapperProps,
	) => {
		setState({ takeOnCallLoading: true });
		try {
			await scheduleEndpoints.takeOnCall({
				accountId,
				scheduleId: getState().schedule.id,
			});
			flagApi.showFlag({
				...successToastProps(),
				title: intlApi.formatMessage(messages.takeOnCallSuccessTitle),
				isAutoDismiss: true,
			});
			dispatch(getTimelineAction());
			closePageModal(PageModalType.TakeOnCall);
		} catch (error) {
			defaultCatch<void>(
				{
					id: 'takeOnCallAction',
					httpErrorHandler: (err) => {
						if (err.message) {
							flagApi.showFlag({
								...errorToastProps(),
								description: err.message,
								title: intlApi.formatMessage(commonMessages.error),
							});
						} else {
							flagApi.showFlag({
								...errorToastProps(),
								title: intlApi.formatMessage(messages.takeOnCallErrorTitle),
							});
						}
					},
				},
				error,
			);
		} finally {
			setState({ takeOnCallLoading: false });
		}
	};

const getOverrideAction =
	({ alias }: Omit<GetOverrideParameters, 'scheduleId'>) =>
	async (
		{ getState }: StoreActionApi<ScheduleStore>,
		{ flagApi, intlApi, defaultCatch }: WrapperProps,
	) => {
		try {
			const override = await scheduleEndpoints.getOverride({
				scheduleId: getState().schedule.id,
				alias,
			});

			return override;
		} catch (error) {
			defaultCatch<void>(
				{
					id: 'getOverrideAction',
					httpErrorHandler: (err) => {
						if (err.message) {
							flagApi.showFlag({
								...errorToastProps(),
								description: err.message,
								title: intlApi.formatMessage(commonMessages.error),
							});
						} else {
							flagApi.showFlag({
								...errorToastProps(),
								title: intlApi.formatMessage(messages.getOverrideErrorTitle),
							});
						}
					},
				},
				error,
			);

			throw error;
		}
	};

const revertOverrideAction =
	({ alias }: Omit<RevertOverrideParameters, 'scheduleId'>) =>
	async (
		{ dispatch, setState, getState }: StoreActionApi<ScheduleStore>,
		{ flagApi, intlApi, closePageModal, defaultCatch }: WrapperProps,
	) => {
		try {
			setState({ revertOverrideLoading: true });
			await scheduleEndpoints.revertOverride({
				scheduleId: getState().schedule.id,
				alias,
			});
			flagApi.showFlag({
				...successToastProps(),
				title: intlApi.formatMessage(messages.revertOverrideSuccessTitle),
				isAutoDismiss: true,
			});
			dispatch(getTimelineAction());
			closePageModal(PageModalType.RevertOverride);
		} catch (error) {
			defaultCatch<void>(
				{
					id: 'revertOverrideAction',
					httpErrorHandler: (err) => {
						if (err.message) {
							flagApi.showFlag({
								...errorToastProps(),
								description: err.message,
								title: intlApi.formatMessage(commonMessages.error),
							});
						} else {
							flagApi.showFlag({
								...errorToastProps(),
								title: intlApi.formatMessage(messages.revertOverrideErrorTitle),
							});
						}
					},
				},
				error,
			);
		} finally {
			setState({ revertOverrideLoading: false });
		}
	};

// TODO: consider using different loading state variable for add override, add override bulk and update override
const addOverrideAction =
	({
		rotationIds,
		startTime,
		responder,
		timezone,
		endTime,
	}: Omit<AddOverrideParameters, 'scheduleId'>) =>
	async (
		{ dispatch, setState, getState }: StoreActionApi<ScheduleStore>,
		{ flagApi, intlApi, closePageModal, defaultCatch, createAnalyticsEvent }: WrapperProps,
	) => {
		setState({ addOverrideLoading: true });
		const event = createAnalyticsEvent({});

		try {
			await scheduleEndpoints.addOverride({
				scheduleId: getState().schedule.id,
				rotationIds,
				startTime,
				responder,
				timezone,
				endTime,
			});
			flagApi.showFlag({
				...successToastProps(),
				title: intlApi.formatMessage(messages.addOverrideSuccessTitle),
				isAutoDismiss: true,
			});
			dispatch(getTimelineAction());
			closePageModal(PageModalType.AddOverride);

			fireTrackAnalytics(event, 'overrideCreation succeeded', getState().schedule.id);
		} catch (error) {
			defaultCatch<void>(
				{
					id: 'addOverrideAction',
					httpErrorHandler: (err) => {
						if (err.message) {
							flagApi.showFlag({
								...errorToastProps(),
								description: err.message,
								title: intlApi.formatMessage(commonMessages.error),
							});
						} else {
							flagApi.showFlag({
								...errorToastProps(),
								title: intlApi.formatMessage(messages.addOverrideErrorTitle),
							});
						}
					},
				},
				error,
			);

			fireTrackAnalytics(event, 'overrideCreation failed', getState().schedule.id);
		} finally {
			setState({ addOverrideLoading: false });
		}
	};

const updateOverrideAction =
	({
		rotationIds,
		startTime,
		responder,
		timezone,
		endTime,
		alias,
	}: Omit<UpdateOverrideParameters, 'scheduleId'>) =>
	async (
		{ dispatch, setState, getState }: StoreActionApi<ScheduleStore>,
		{ flagApi, intlApi, closePageModal, defaultCatch, createAnalyticsEvent }: WrapperProps,
	) => {
		setState({ addOverrideLoading: true });
		try {
			await scheduleEndpoints.updateOverride({
				scheduleId: getState().schedule.id,
				rotationIds,
				startTime,
				responder,
				timezone,
				endTime,
				alias,
			});
			flagApi.showFlag({
				...successToastProps(),
				title: intlApi.formatMessage(messages.updateOverrideSuccessTitle),
				isAutoDismiss: true,
			});
			dispatch(getTimelineAction());
			closePageModal(PageModalType.AddOverride);

			const event = createAnalyticsEvent({});
			fireTrackAnalytics(event, 'override updated', getState().schedule.id);
		} catch (error) {
			defaultCatch<void>(
				{
					id: 'updateOverrideAction',
					httpErrorHandler: (err) => {
						if (err.message) {
							flagApi.showFlag({
								...errorToastProps(),
								description: err.message,
								title: intlApi.formatMessage(commonMessages.error),
							});
						} else {
							flagApi.showFlag({
								...errorToastProps(),
								title: intlApi.formatMessage(messages.updateOverrideErrorTitle),
							});
						}
					},
				},
				error,
			);
		} finally {
			setState({ addOverrideLoading: false });
		}
	};

const addOverrideBulkAction =
	({ overrides }: Omit<AddOverrideBulkParameters, 'scheduleId'>) =>
	async (
		{ dispatch, setState, getState }: StoreActionApi<ScheduleStore>,
		{ flagApi, intlApi, defaultCatch }: WrapperProps,
	) => {
		setState({ addOverrideLoading: true });
		try {
			await scheduleEndpoints.addOverrideBulk({
				scheduleId: getState().schedule.id,
				overrides,
			});
			flagApi.showFlag({
				...successToastProps(),
				title: intlApi.formatMessage(messages.addOverrideBulkSuccessTitle),
				isAutoDismiss: true,
			});
			dispatch(getTimelineAction());
		} catch (error) {
			defaultCatch<void>(
				{
					id: 'addOverrideBulkAction',
					httpErrorHandler: (err) => {
						if (err.message) {
							flagApi.showFlag({
								...errorToastProps(),
								description: err.message,
								title: intlApi.formatMessage(commonMessages.error),
							});
						} else {
							flagApi.showFlag({
								...errorToastProps(),
								title: intlApi.formatMessage(messages.addOverrideBulkErrorTitle),
							});
						}
					},
				},
				error,
			);
		} finally {
			setState({ addOverrideLoading: false });
		}
	};

const todayAction =
	() =>
	async ({ getState, setState, dispatch }: StoreActionApi<ScheduleStore>) => {
		setState({
			currentDate: zoned.midOfDay(new Date(), getState().schedule.timezone),
		});

		dispatch(getTimelineAction());
	};

export const actions = {
	addOverrideAction,
	addOverrideBulkAction,
	getOverrideAction,
	revertOverrideAction,
	updateOverrideAction,
	getTimelineAction,
	nextPeriodAction,
	previousPeriodAction,
	setSelectedViewAction,
	setRotationAction,
	setTimelineTypeAction,
	takeOnCallAction,
	todayAction,
};
